// **The ambient loop's own recorded-snapshot format** (`client-home`; plan **D14**).
//
// Home's background is a pre-simulated colony loop replayed through the shipped world renderer.
// It began life reusing the production trace format and that was wrong twice over:
//
//   * **Machinery it does not use.** The trace format carries a two-tier index, per-component
//     checksums and a manifest commit marker, all of which exist for *seekable, multi-colony,
//     graded* replay. A looping menu backdrop plays forward forever and seeks nowhere.
//   * **A dependency it must not have.** `rounds-trace.ts` rejects any version but its exact
//     supported one, and `rounds-balance` moves that format v1 → v2 with a new required
//     component. Reusing it would have made a 72-task change a hard gate on a repair to a mode
//     that is currently unreachable.
//
// So this is a deliberately minimal, self-contained format: **positions, a type, and a frame
// index — nothing seekable.** It reads no manifest, no tier index and no component checksum, and
// it imports nothing from `net/rounds-trace.ts`, so a trace-format move cannot reach it. The cost
// is one small extra serialization format for a decorative asset; the note in
// `docs/dev/ambient-background.md` records the pinned generator command that writes it.
//
// ## Layout (little-endian)
//
// ```text
// magic        4  "WGAM"
// version      1  == AMBIENT_FORMAT_VERSION
// tickRateHz   1  the rate the frames were sampled at
// fieldSize    2  world units across the square field
// frameCount   2
// cellCount    2  hive cells — static for the whole loop
// patchCount   2  flower patches — static positions
// cells        cellCount   × { u16 x, u16 y }
// patches      patchCount  × { u16 x, u16 y }
// frames       frameCount  × { u16 beeCount, beeCount × { u16 id, u16 x, u16 y, u8 typeId } }
// ```
//
// Positions are quantized to a `u16` grid over `[-field/2, +field/2]` — the same arithmetic the
// close-LOD wire tier uses (PROTOCOL §4), restated here rather than imported, because importing
// it is precisely the coupling **D14** removes. Bee **ids are carried** so the renderer's facing
// deriver can track a bee across frames; without them every bee's heading would jitter.
//
// The decoder **never throws** and never repairs: any failure at all — bad magic, an unsupported
// version, a truncated body, trailing bytes — returns `null`, which the background reads as
// "degrade to the static fill" (**D10**). Silence at runtime is not silence in CI: the asset's
// checksum and byte budget are checked by tests, so a break the fallback hides is still caught.

export const AMBIENT_FORMAT_VERSION = 1;

/** `"WGAM"`. Deliberately distinct from any trace magic — these are different formats with
 * different owners, and a reader that accepted both would re-create the coupling. */
const MAGIC = [0x57, 0x47, 0x41, 0x4d] as const;

const HEADER_BYTES = 4 + 1 + 1 + 2 + 2 + 2 + 2;
const POINT_BYTES = 4;
const BEE_BYTES = 7;

/** The `u16` grid every position is expressed on. */
const GRID = 65_535;

/** A world coordinate onto the `u16` grid over `[-field/2, +field/2]`. */
export function quantizeAmbientPosition(value: number, fieldSize: number): number {
  const unit = (value + fieldSize / 2) / fieldSize;
  const clamped = Math.min(1, Math.max(0, unit));
  return Math.round(clamped * GRID);
}

/** The inverse of {@link quantizeAmbientPosition}. */
export function dequantizeAmbientPosition(q: number, fieldSize: number): number {
  return (q / GRID) * fieldSize - fieldSize / 2;
}

export interface AmbientBee {
  id: number;
  /** World units. */
  position: readonly [number, number];
  /** The bee's type ordinal, used for its palette colour. */
  typeId: number;
}

export interface AmbientFrame {
  bees: readonly AmbientBee[];
}

export interface AmbientLoop {
  version: number;
  /** World units across the square field — the renderer's projection needs it. */
  fieldSize: number;
  /** The rate the frames were sampled at, which is also the rate they are played back at. */
  tickRateHz: number;
  /** Hive cell centres in world units, static for the whole loop. */
  cells: readonly (readonly [number, number])[];
  /** Flower-patch centres in world units, static for the whole loop. */
  patches: readonly (readonly [number, number])[];
  frames: readonly AmbientFrame[];
}

/**
 * Encodes a loop.
 *
 * This lives beside the decoder on purpose: the transcoder that builds the committed asset from
 * `waggle-simlab`'s snapshot stream (`client/tools/build-ambient-loop.mjs`) calls it, and so do
 * the background's tests. One implementation of the format, not a Rust writer and a TypeScript
 * reader that can silently disagree.
 */
export function encodeAmbientLoop(loop: AmbientLoop): Uint8Array {
  const beeTotal = loop.frames.reduce((sum, frame) => sum + frame.bees.length, 0);
  const size =
    HEADER_BYTES +
    (loop.cells.length + loop.patches.length) * POINT_BYTES +
    loop.frames.length * 2 +
    beeTotal * BEE_BYTES;
  const bytes = new Uint8Array(size);
  const view = new DataView(bytes.buffer);
  let at = 0;

  bytes.set(MAGIC, at);
  at += 4;
  view.setUint8(at, AMBIENT_FORMAT_VERSION);
  at += 1;
  view.setUint8(at, loop.tickRateHz);
  at += 1;
  view.setUint16(at, loop.fieldSize, true);
  at += 2;
  view.setUint16(at, loop.frames.length, true);
  at += 2;
  view.setUint16(at, loop.cells.length, true);
  at += 2;
  view.setUint16(at, loop.patches.length, true);
  at += 2;

  const writePoint = (point: readonly [number, number]): void => {
    view.setUint16(at, quantizeAmbientPosition(point[0], loop.fieldSize), true);
    view.setUint16(at + 2, quantizeAmbientPosition(point[1], loop.fieldSize), true);
    at += POINT_BYTES;
  };
  for (const cell of loop.cells) {
    writePoint(cell);
  }
  for (const patch of loop.patches) {
    writePoint(patch);
  }
  for (const frame of loop.frames) {
    view.setUint16(at, frame.bees.length, true);
    at += 2;
    for (const bee of frame.bees) {
      view.setUint16(at, bee.id, true);
      view.setUint16(at + 2, quantizeAmbientPosition(bee.position[0], loop.fieldSize), true);
      view.setUint16(at + 4, quantizeAmbientPosition(bee.position[1], loop.fieldSize), true);
      view.setUint8(at + 6, bee.typeId);
      at += BEE_BYTES;
    }
  }
  return bytes;
}

/**
 * Decodes a loop, or returns `null` on **any** failure.
 *
 * Fail-soft is the whole contract here: this feeds decoration, and a background that throws would
 * take the entry screen down with it. Every bound is checked before it is read, so a truncated or
 * hostile file cannot walk off the end of the buffer.
 */
export function decodeAmbientLoop(source: ArrayBuffer | Uint8Array): AmbientLoop | null {
  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source);
  if (bytes.byteLength < HEADER_BYTES) {
    return null;
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let i = 0; i < MAGIC.length; i += 1) {
    if (view.getUint8(i) !== MAGIC[i]) {
      return null;
    }
  }
  const version = view.getUint8(4);
  if (version !== AMBIENT_FORMAT_VERSION) {
    return null;
  }
  const tickRateHz = view.getUint8(5);
  const fieldSize = view.getUint16(6, true);
  const frameCount = view.getUint16(8, true);
  const cellCount = view.getUint16(10, true);
  const patchCount = view.getUint16(12, true);
  if (tickRateHz === 0 || fieldSize === 0) {
    return null;
  }

  let at = HEADER_BYTES;
  const readPoints = (count: number): [number, number][] | null => {
    if (at + count * POINT_BYTES > bytes.byteLength) {
      return null;
    }
    const points: [number, number][] = [];
    for (let i = 0; i < count; i += 1) {
      points.push([
        dequantizeAmbientPosition(view.getUint16(at, true), fieldSize),
        dequantizeAmbientPosition(view.getUint16(at + 2, true), fieldSize),
      ]);
      at += POINT_BYTES;
    }
    return points;
  };

  const cells = readPoints(cellCount);
  if (cells === null) {
    return null;
  }
  const patches = readPoints(patchCount);
  if (patches === null) {
    return null;
  }

  const frames: AmbientFrame[] = [];
  for (let f = 0; f < frameCount; f += 1) {
    if (at + 2 > bytes.byteLength) {
      return null;
    }
    const beeCount = view.getUint16(at, true);
    at += 2;
    if (at + beeCount * BEE_BYTES > bytes.byteLength) {
      return null;
    }
    const bees: AmbientBee[] = [];
    for (let b = 0; b < beeCount; b += 1) {
      bees.push({
        id: view.getUint16(at, true),
        position: [
          dequantizeAmbientPosition(view.getUint16(at + 2, true), fieldSize),
          dequantizeAmbientPosition(view.getUint16(at + 4, true), fieldSize),
        ],
        typeId: view.getUint8(at + 6),
      });
      at += BEE_BYTES;
    }
    frames.push({ bees });
  }
  // Trailing bytes mean the file is not what its header says it is. Refuse rather than render a
  // guess: a silent partial read is exactly the failure the CI checks exist to catch.
  if (at !== bytes.byteLength) {
    return null;
  }
  return { version, fieldSize, tickRateHz, cells, patches, frames };
}
